import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { GlassCard, GlassButton, GlassInput, ModuleHeader } from '../ui';
import { Wand2, Loader2, LayoutTemplate, Layers, Eye, X } from 'lucide-react';
import { apiClient, getProjects, getBrandGuides, Project, BrandGuide } from '../../services/api';
import './SiteBuilder.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SiteTemplate {
  template_id: string;
  name: string;
  description: string;
  tags: string[];
  featured: boolean;
  use_count: number;
  section_count: number;
  page_count: number;
  thumbnail_url?: string;
}

const INDUSTRY_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Legal', value: 'legal' },
  { label: 'Financial', value: 'financial' },
  { label: 'Real Estate', value: 'real-estate' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Technology', value: 'technology' },
  { label: 'SaaS', value: 'saas' },
  { label: 'Retail', value: 'retail' },
  { label: 'Consulting', value: 'consulting' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Fitness', value: 'fitness' },
  { label: 'Education', value: 'education' },
  { label: 'Ecommerce', value: 'ecommerce' },
  { label: 'Beauty', value: 'beauty' },
  { label: 'Trades', value: 'trades' },
  { label: 'Automotive', value: 'automotive' },
  { label: 'Non-Profit', value: 'non-profit' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Health', value: 'health' },
];

const INDUSTRY_LABELS: Record<string, string> = {
  'legal-law-firm': 'Legal',
  'financial-services': 'Financial',
  'real-estate-agency': 'Real Estate',
  'health-wellness': 'Health',
  'professional-services': 'Consulting',
  'saas-tech': 'SaaS',
  'marketing-agency': 'Marketing',
  'trades-local': 'Trades',
};

function getIndustryLabel(template: SiteTemplate): string {
  if (INDUSTRY_LABELS[template.name]) return INDUSTRY_LABELS[template.name];
  const known = ['legal', 'financial', 'real-estate', 'health', 'consulting', 'saas', 'marketing', 'trades'];
  for (const tag of template.tags) {
    const match = known.find(k => tag.toLowerCase().includes(k));
    if (match) return match.charAt(0).toUpperCase() + match.slice(1);
  }
  return 'General';
}

// ---------------------------------------------------------------------------
// Template Browser Tab
// ---------------------------------------------------------------------------

const TemplateBrowser: React.FC<{
  projects: Project[];
  brandGuides: BrandGuide[];
  selectedProjectId?: string;
}> = ({ projects, brandGuides, selectedProjectId }) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<SiteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [projectId, setProjectId] = useState(selectedProjectId || (projects[0]?.project_id ?? ''));
  const [brandGuideId, setBrandGuideId] = useState('');
  const [usingTemplate, setUsingTemplate] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<SiteTemplate | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!projectId && projects.length) setProjectId(projects[0].project_id);
  }, [projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the project's default brand guide when project changes — falls
  // back to the first guide on the project. Backend has the same chain (default
  // → any) but pre-filling the dropdown lets the user see what's about to apply
  // and override before clicking 'Use Template'.
  useEffect(() => {
    if (!projectId) return;
    const projectGuides = brandGuides.filter((b: any) => b.project_id === projectId);
    if (!projectGuides.length) return;
    const defaultGuide = projectGuides.find((b: any) => b.is_default) || projectGuides[0];
    if (defaultGuide && !brandGuideId) {
      setBrandGuideId(defaultGuide.brand_guide_id);
    }
  }, [projectId, brandGuides]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    const params = activeFilter !== 'all' ? `?industry=${encodeURIComponent(activeFilter)}` : '';
    apiClient.get(`/site-builder/templates${params}`)
      .then(res => setTemplates(res.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [activeFilter]);

  const handleUseTemplate = async (template: SiteTemplate) => {
    if (!projectId) { setError('Please select a project first.'); return; }
    setUsingTemplate(template.template_id);
    setError('');
    try {
      const siteName = `${getIndustryLabel(template)} Site`;
      const res = await apiClient.post('/site-builder/sites/from-template', {
        template_id: template.template_id,
        project_id: projectId,
        name: siteName,
        brand_guide_id: brandGuideId || null,
      });
      if (res.data?.site_project_id) {
        navigate(`/site-builder/${res.data.site_project_id}`);
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to create site from template.'));
    } finally {
      setUsingTemplate(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Project + Brand selectors */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Project</label>
          <select value={projectId} onChange={e => setProjectId(e.target.value)} style={{
            width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)',
            borderRadius: 8, background: 'var(--panel)', fontSize: '0.875rem', color: 'var(--text)',
          }}>
            <option value="">Select project...</option>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Brand Guide (optional)</label>
          <select value={brandGuideId} onChange={e => setBrandGuideId(e.target.value)} style={{
            width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)',
            borderRadius: 8, background: 'var(--panel)', fontSize: '0.875rem', color: 'var(--text)',
          }}>
            <option value="">No brand guide</option>
            {brandGuides.map(b => <option key={b.brand_guide_id} value={b.brand_guide_id}>{b.brand_name || b.brand_guide_name}</option>)}
          </select>
        </div>
      </div>

      {/* Industry filter pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {INDUSTRY_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setActiveFilter(f.value)}
            style={{
              padding: '6px 14px', borderRadius: 9999, border: 'none', cursor: 'pointer',
              fontSize: '0.8125rem', fontWeight: 500, transition: 'all 0.15s',
              background: activeFilter === f.value ? 'var(--brand-primary, #2563eb)' : 'var(--panel, #f8fafc)',
              color: activeFilter === f.value ? '#fff' : 'var(--text-secondary, #64748b)',
              boxShadow: activeFilter === f.value ? '0 2px 8px rgba(37,99,235,0.3)' : '0 1px 3px rgba(0,0,0,0.07)',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ color: '#ef4444', fontSize: '0.875rem', background: '#fef2f2', padding: '8px 12px', borderRadius: 6 }}>
          {error}
        </div>
      )}

      {/* Template grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Loader2 size={28} className="spin" color="var(--brand-primary, #2563eb)" />
        </div>
      ) : templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
          <LayoutTemplate size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>No templates found for this filter.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {templates.map(t => {
            const isLoading = usingTemplate === t.template_id;
            const industryLabel = getIndustryLabel(t);
            return (
              <div
                key={t.template_id}
                style={{
                  background: 'var(--panel, #fff)',
                  border: '1px solid var(--stroke, rgba(0,0,0,0.08))',
                  borderRadius: 12,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  transition: 'box-shadow 0.15s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
                onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)')}
              >
                {/* Thumbnail */}
                <div className="sb-thumb-wrap">
                  {t.thumbnail_url ? (
                    <img
                      src={t.thumbnail_url}
                      alt={t.name}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%', height: '100%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', background: 'var(--panel-strong, #f1f5f9)',
                      color: 'var(--text-secondary)', fontSize: '0.8rem',
                    }}>
                      No preview
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                  {/* Badges row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 9999,
                      background: 'rgba(37,99,235,0.08)', color: 'var(--brand-primary, #2563eb)',
                      fontSize: '0.75rem', fontWeight: 600,
                    }}>
                      {industryLabel}
                    </span>
                    {t.featured && (
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, background: '#fef9c3',
                        color: '#a16207', fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                      }}>
                        ★ Featured
                      </span>
                    )}
                  </div>

                  {/* Name */}
                  <h3 style={{ fontSize: '1rem', margin: 0, textTransform: 'capitalize' }}>
                    {t.name.replace(/-/g, ' ')}
                  </h3>

                  {/* Description */}
                  <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
                    {t.description}
                  </p>

                  {/* Meta */}
                  <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Layers size={12} /> {t.page_count} pages
                    </span>
                    <span>{t.section_count} sections</span>
                  </div>

                  {/* Buttons */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button
                      onClick={() => setPreviewTemplate(t)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 8, border: '1px solid var(--stroke)',
                        background: 'var(--panel)', color: 'var(--text)', cursor: 'pointer',
                        fontSize: '0.8125rem', fontWeight: 500, flexShrink: 0,
                      }}
                    >
                      <Eye size={14} /> Preview
                    </button>
                    <GlassButton
                      onClick={() => handleUseTemplate(t)}
                      disabled={isLoading || !!usingTemplate}
                      style={{ flex: 1 }}
                    >
                      {isLoading ? (
                        <><Loader2 size={14} className="spin" style={{ marginRight: 6 }} /> Creating...</>
                      ) : (
                        'Use Template'
                      )}
                    </GlassButton>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Full-size preview modal — portaled to body to escape stacking contexts */}
      {previewTemplate && createPortal(
        <div
          onClick={() => setPreviewTemplate(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--panel, #fff)', borderRadius: 12,
              width: '90vw', maxWidth: 1100, display: 'flex', flexDirection: 'column',
              maxHeight: '92vh', overflow: 'hidden',
              boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px', borderBottom: '1px solid var(--stroke)',
              flexShrink: 0,
            }}>
              <span style={{ fontWeight: 600, fontSize: '1rem', textTransform: 'capitalize' }}>
                {previewTemplate.name.replace(/-/g, ' ')}
              </span>
              <button
                onClick={() => setPreviewTemplate(null)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-secondary)', padding: 4, borderRadius: 6,
                  display: 'flex', alignItems: 'center',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal iframe */}
            <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <iframe
                src={`/api/site-builder/templates/${previewTemplate.template_id}/preview`}
                style={{ width: '100%', height: '100%', border: 'none', minHeight: '70vh' }}
                title={`Full preview of ${previewTemplate.name}`}
              />
            </div>

            {/* Modal footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              padding: '12px 20px', borderTop: '1px solid var(--stroke)', flexShrink: 0,
            }}>
              <button
                onClick={() => setPreviewTemplate(null)}
                style={{
                  padding: '8px 18px', borderRadius: 8, border: '1px solid var(--stroke)',
                  background: 'var(--panel)', color: 'var(--text)', cursor: 'pointer',
                  fontSize: '0.875rem', fontWeight: 500,
                }}
              >
                Close
              </button>
              <GlassButton
                onClick={() => { setPreviewTemplate(null); handleUseTemplate(previewTemplate); }}
                disabled={!!usingTemplate}
              >
                {usingTemplate === previewTemplate.template_id ? (
                  <><Loader2 size={14} className="spin" style={{ marginRight: 6 }} /> Creating...</>
                ) : (
                  'Use Template'
                )}
              </GlassButton>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main GenerationWizard
// ---------------------------------------------------------------------------

const GenerationWizard: React.FC = () => {
  const navigate = useNavigate();
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const selectedProjectId = ctx?.selectedProjectId;

  const [activeTab, setActiveTab] = useState<'template' | 'ai'>('template');

  const [projects, setProjects] = useState<Project[]>([]);
  const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
  const [projectId, setProjectId] = useState('');
  const [brandGuideId, setBrandGuideId] = useState('');
  const [brief, setBrief] = useState('');
  const [industry, setIndustry] = useState('');
  const [audience, setAudience] = useState('');
  const [pages, setPages] = useState('Home, About, Services, Contact');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getProjects().then(data => {
      const active = data.filter(p => !p.is_archived);
      setProjects(active);
      if (selectedProjectId && active.some(p => p.project_id === selectedProjectId)) {
        setProjectId(selectedProjectId);
      } else if (active.length > 0) {
        setProjectId(active[0].project_id);
      }
    }).catch(() => {});
    getBrandGuides().then(data => {
      setBrandGuides(data || []);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    if (!projectId || !brief.trim()) {
      setError('Please select a project and enter a brief.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await apiClient.post('/site-builder/generate', {
        project_id: projectId,
        brief: brief.trim(),
        brand_guide_id: brandGuideId || null,
        industry: industry.trim(),
        target_audience: audience.trim(),
        pages: pages.split(',').map(p => p.trim()).filter(Boolean),
      }, { timeout: 300000 });
      if (res.data?.site_project_id) {
        navigate(`/site-builder/${res.data.site_project_id}`);
      } else {
        setError('Generation completed but returned no site. Please try again.');
      }
    } catch (err: any) {
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        setError('Generation timed out — the AI is taking longer than expected. Please try with fewer pages or try again.');
      } else {
        setError(getApiError(err, err.message || 'Generation failed. Please try again.'));
      }
    } finally {
      setGenerating(false);
    }
  };

  // Tab button styles
  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: '0.875rem', transition: 'all 0.15s',
    background: active ? 'var(--brand-primary, #2563eb)' : 'transparent',
    color: active ? '#fff' : 'var(--text-secondary, #64748b)',
  });

  return (
    <div style={{ padding: '0 0 40px' }}>
      <ModuleHeader title="Site Builder" breadcrumb={['Site Builder', 'New Site']} />

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px' }}>
        <GlassCard style={{ padding: 32 }}>
          {/* Tab switcher */}
          <div style={{
            display: 'flex', gap: 4, background: 'var(--panel, #f8fafc)',
            border: '1px solid var(--stroke, rgba(0,0,0,0.08))',
            borderRadius: 10, padding: 4, marginBottom: 28,
          }}>
            <button style={tabStyle(activeTab === 'template')} onClick={() => setActiveTab('template')}>
              <LayoutTemplate size={16} style={{ marginRight: 7, verticalAlign: 'middle' }} />
              Start from Template
            </button>
            <button style={tabStyle(activeTab === 'ai')} onClick={() => setActiveTab('ai')}>
              <Wand2 size={16} style={{ marginRight: 7, verticalAlign: 'middle' }} />
              Generate with AI
            </button>
          </div>

          {/* Template tab */}
          {activeTab === 'template' && (
            <TemplateBrowser
              projects={projects}
              brandGuides={brandGuides}
              selectedProjectId={selectedProjectId}
            />
          )}

          {/* AI Generation tab */}
          {activeTab === 'ai' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
                <Wand2 size={24} color="var(--brand-primary, #2563eb)" />
                <div>
                  <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Generate with AI</h2>
                  <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '0.875rem' }}>
                    Describe your business and AI will build a multi-page site
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Project</label>
                  <select value={projectId} onChange={e => setProjectId(e.target.value)} style={{
                    width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)',
                    borderRadius: 8, background: 'var(--panel)', fontSize: '0.875rem', color: 'var(--text)',
                  }}>
                    <option value="">Select project...</option>
                    {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Brand Guide (optional)</label>
                  <select value={brandGuideId} onChange={e => setBrandGuideId(e.target.value)} style={{
                    width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)',
                    borderRadius: 8, background: 'var(--panel)', fontSize: '0.875rem', color: 'var(--text)',
                  }}>
                    <option value="">No brand guide</option>
                    {brandGuides.map(b => <option key={b.brand_guide_id} value={b.brand_guide_id}>{b.brand_name || b.brand_guide_name}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Business Brief</label>
                  <textarea
                    value={brief}
                    onChange={e => setBrief(e.target.value)}
                    placeholder="Describe your business, services, target audience, and what you want the website to achieve..."
                    rows={5}
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid var(--stroke)',
                      borderRadius: 8, background: 'var(--panel)', fontSize: '0.875rem',
                      resize: 'vertical', color: 'var(--text)',
                    }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Industry</label>
                    <GlassInput value={industry} onChange={setIndustry} placeholder="e.g. Financial Services" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Target Audience</label>
                    <GlassInput value={audience} onChange={setAudience} placeholder="e.g. Small business owners" />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6 }}>Pages (comma-separated)</label>
                  <GlassInput value={pages} onChange={setPages} placeholder="Home, About, Services, Contact" />
                </div>

                {error && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', background: '#fef2f2', padding: '8px 12px', borderRadius: 6 }}>
                    {error}
                  </div>
                )}

                <GlassButton
                  onClick={handleGenerate}
                  disabled={generating}
                  style={{ width: '100%', marginTop: 8 }}
                >
                  {generating ? (
                    <><Loader2 size={18} className="spin" style={{ marginRight: 8 }} /> Generating site...</>
                  ) : (
                    <><Wand2 size={18} style={{ marginRight: 8 }} /> Generate Website</>
                  )}
                </GlassButton>
              </div>
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
};

export default GenerationWizard;
